/**
 * In-memory cache of optimistic Caption Builder pins, keyed by matchId.
 *
 * The Caption Builder remounts on every photo switch (IptcPanel keys
 * its inner component on imageId), and `quick_captions_pinned` is
 * loaded fresh from `matchData.quick_captions_pinned`. On flaky / CORS-
 * blocked networks an optimistic pin's PATCH may never reach the
 * server, so the next mount sees an empty pin list and the chip
 * vanishes. This cache holds those pins client-side until the server
 * acknowledges them — when the PATCH finally succeeds, the cache entry
 * is replaced with the server's record (real id), and unpinning drops
 * it altogether.
 *
 * Lives only in this tab's memory; a hard refresh will lose pending
 * pins (acceptable — the user can re-pin once back online).
 */

const cache = new Map(); // matchId -> [{ id, label, template, ... }]

function key(matchId) {
  return matchId ? String(matchId) : null;
}

export function getPendingPins(matchId) {
  const k = key(matchId);
  if (!k) return [];
  return cache.get(k) || [];
}

/**
 * Insert or merge a pending pin. De-dupes on `id` and `template` —
 * later writes for the same template overwrite the earlier entry.
 */
export function addPendingPin(matchId, pin) {
  const k = key(matchId);
  if (!k || !pin) return;
  const list = cache.get(k) || [];
  const filtered = list.filter((p) => p.id !== pin.id && p.template !== pin.template);
  cache.set(k, [pin, ...filtered]);
}

/**
 * Replace an optimistic pin (still keyed by its local id) with the
 * server's confirmed record. If the optimistic entry is gone (e.g.
 * the user already removed it), insert the server record at the head
 * if the template still matches; otherwise no-op.
 */
export function replacePendingPin(matchId, optimisticId, serverPin) {
  const k = key(matchId);
  if (!k || !serverPin) return;
  const list = cache.get(k) || [];
  let found = false;
  const next = list.map((p) => {
    if (p.id === optimisticId) {
      found = true;
      return serverPin;
    }
    return p;
  });
  if (!found) {
    // Local entry gone — only re-add if no entry already references the same template.
    if (!next.some((p) => p.template === serverPin.template)) {
      next.unshift(serverPin);
    }
  }
  cache.set(k, next);
}

/**
 * Drop a pin from the cache (manual remove via the × button, or after
 * the server unpin succeeds).
 */
export function dropPendingPin(matchId, id) {
  const k = key(matchId);
  if (!k) return;
  const list = cache.get(k) || [];
  cache.set(k, list.filter((p) => p.id !== id));
}

/**
 * Merge server pins (truth) with in-memory pending pins, de-duping by
 * id and template. Used by CaptionBuilder when matchData arrives.
 */
export function mergeServerAndPending(matchId, serverPins) {
  const server = Array.isArray(serverPins) ? serverPins : [];
  const pending = getPendingPins(matchId);
  if (pending.length === 0) return server;
  const seenIds = new Set(server.map((p) => p.id).filter(Boolean));
  const seenTemplates = new Set(server.map((p) => p.template).filter(Boolean));
  const extras = pending.filter(
    (p) => (!p.id || !seenIds.has(p.id)) && (!p.template || !seenTemplates.has(p.template)),
  );
  return [...server, ...extras];
}
